import { useEffect, useState } from 'react'
import { OptinMonsterEvent } from '@chegg-wt/core/state/instrumentation/types'
import { AuthSuccessGrant } from '@chegg/wtf-components'
import { AUTH_FRAME_AUTH_SUCCESSFUL } from '@chegg-wt/core/components/Modals/AuthModal/types'
import { trackWithoutThrow } from '@chegg-wt/core/utils/errors'
import { isWindowDefined } from '@chegg-wt/core/utils/isWindowDefined'
import { NextJsRoute } from '../types/core'
import usePostMessageListener from './usePostMessageListener'

/* These events fire when OIM campaigns are initialized / actually displayed
   See https://optinmonster.com/docs/optinmonster-javascript-events-api/
 */
const OIM_CAMPAIGN_INIT_EVENT = 'om.Campaign.init'
export const OIM_CAMPAIGN_SHOW_EVENT = 'om.Campaign.startShow'

/* https://docs.developers.optimizely.com/web/docs/bucket-visitor */
interface BucketVisitor {
  type: string
  experimentId: string
  variationId: string
}
/* https://docs.developers.optimizely.com/web/docs/page */
interface PageObject {
  type: string
  pageName: string
}
interface OptimizelyObject {
  optimizelyBucket: BucketVisitor
  optimizelyPage: PageObject
}

export type OptimizelyMapper = (campaignId: string) => OptimizelyObject

export const useOimOptIntegrationHandlers = (optimizelyMapper: OptimizelyMapper) => {
  /**
   * OIM <> Optimizely Web integration
   * When a particular campaign is shown, force bucket into
   * the corresponding Optimizely Web experiment
   */
  useEffect(() => {
    const oimWidgetShowHandler = (event: OptinMonsterEvent) => {
      if (!isWindowDefined) return undefined
      const optimizely = window.optimizely || []

      const campaignId = event?.detail?.Campaign?.id
      const { optimizelyBucket, optimizelyPage } = optimizelyMapper(campaignId)

      optimizely.push(optimizelyBucket)
      optimizely.push(optimizelyPage)
    }

    document.addEventListener(OIM_CAMPAIGN_SHOW_EVENT, oimWidgetShowHandler, false)

    return () =>
      document.removeEventListener(OIM_CAMPAIGN_SHOW_EVENT, oimWidgetShowHandler, false)
  })
}

const OIM_AUTH_CAMPAIGN_KEY = 'superauth-iframe'

interface OimAuthEventHandlerProps {
  login: (authSuccessGrant: AuthSuccessGrant, redirectTo?: NextJsRoute) => void
  isAuthLoading: boolean
}

export const useOimAuthEventHandlers = ({
  login,
  isAuthLoading,
}: OimAuthEventHandlerProps) => {
  const [oimAuthCampaignId, setOimAuthCampaignId] = useState(null)
  const [loginStarted, setLoginStarted] = useState(false)

  /**
   *  Listeners for setting the OIM auth campaign ID and logging in
   */
  useEffect(() => {
    const oimAuthCampaignHandler = (event: OptinMonsterEvent) => {
      const campaign = event?.detail?.Campaign
      if (campaign?.id && campaign?.name?.includes(OIM_AUTH_CAMPAIGN_KEY)) {
        setOimAuthCampaignId(campaign.id)
      }
    }

    document.addEventListener(OIM_CAMPAIGN_INIT_EVENT, oimAuthCampaignHandler, false)

    return () => {
      document.removeEventListener(OIM_CAMPAIGN_INIT_EVENT, oimAuthCampaignHandler, false)
    }
  })

  usePostMessageListener({
    messageHandler: ({ type, payload }) => {
      if (type === AUTH_FRAME_AUTH_SUCCESSFUL) {
        const campaignObject = window[`om${oimAuthCampaignId}`] || {}
        const campaignConvert = campaignObject?.Listeners?.convert
        if (typeof campaignConvert === 'function') {
          try {
            campaignConvert()
          } catch (e) {
            trackWithoutThrow(e)
          }
        }

        login(payload)
        setLoginStarted(true)
      }
    },
    selector: '#oim-auth-frame',
  })

  /**
   *  Removes the OIM SuperAuth iframe only once auth loading is complete
   */
  useEffect(() => {
    if (loginStarted && !isAuthLoading) {
      const oimModal = document.querySelector(`#om-${oimAuthCampaignId}`)
      if (oimModal) oimModal.remove()
    }
  }, [isAuthLoading, loginStarted, oimAuthCampaignId])
}
